import { createContext, ReactNode, useContext } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { ExternalIntegrationVendor } from 'dashboard/models/external-integration-vendor';
import { GraphQueryTemplateStatus } from 'dashboard/models/graph-query-template';
import { VerificationTemplateStatus } from 'dashboard/models/verification-template';
import { WorkflowActionMeta } from 'dashboard/models/workflow';
import { showMessage } from 'dashboard/redux/actions/messages';
import { getWorkflowActionMeta } from 'dashboard/redux/selectors/workflow-action-meta';
import { RootState } from 'dashboard/redux/store';
import { loadAccountStatuses } from 'dashboard/redux/thunk-actions/account-statuses';
import { loadAccountTypes } from 'dashboard/redux/thunk-actions/account-types';
import { loadCaseQueuesList } from 'dashboard/redux/thunk-actions/case-queues';
import { loadCaseTemplatesList } from 'dashboard/redux/thunk-actions/case-templates';
import { loadExternalIntegrations } from 'dashboard/redux/thunk-actions/external-integrations';
import { loadGraphsAndGraph } from 'dashboard/redux/thunk-actions/graph';
import { loadGraphQueryTemplates } from 'dashboard/redux/thunk-actions/graph-query-templates';
import { loadLists } from 'dashboard/redux/thunk-actions/list';
import {
  loadNewAndLegacyInquiryTemplateList,
  loadThemeList,
} from 'dashboard/redux/thunk-actions/organization';
import { loadReportTemplatesList } from 'dashboard/redux/thunk-actions/report-templates';
import { loadTags } from 'dashboard/redux/thunk-actions/tags';
import { loadThemeSets } from 'dashboard/redux/thunk-actions/theme-sets';
import {
  loadTransactionStatuses,
  loadTransactionStatusesByTransactionType,
} from 'dashboard/redux/thunk-actions/transaction-statuses';
import { loadTransactionTypesList } from 'dashboard/redux/thunk-actions/transaction-types';
import { loadUserList } from 'dashboard/redux/thunk-actions/user';
import { loadVerificationTemplates } from 'dashboard/redux/thunk-actions/verification-templates';
import { validateWorkflow } from 'dashboard/redux/thunk-actions/workflow';
import { validateWorkflowBlock } from 'dashboard/redux/thunk-actions/workflow-block';
import { loadWorkflows } from 'dashboard/redux/thunk-actions/workflows';
import useApiLoader from 'lib/hooks/useApiLoader';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError } from 'lib/permissions-errors';

export const WorkflowActionMetaContext = createContext<WorkflowActionMeta>({
  'account-statuses': [],
  'account-identifiers': [],
  'account-identifiers-unique': [],
  'account-types': [],
  'case-states': [],
  'case-templates': [],
  'case-queues': [],
  'external-integrations': [],
  graph: null,
  'graph-query-templates': [],
  'inquiry-template-version': {},
  'inquiry-templates': [],
  lists: [],
  'report-templates': [],
  [ApiModel.ReportTemplateEquifaxOneview]: [],
  [ApiModel.ReportTemplateKyckrBusinessLookup]: [],
  [ApiModel.ReportTemplateMiddeskBusinessLookup]: [],
  [ApiModel.ReportTemplateSentilinkApplicationRisk]: [],
  [ApiModel.ReportTemplateSentilinkScores]: [],
  [ApiModel.ReportTemplateChainalysisAddressScreening]: [],
  [ApiModel.ReportTemplateMxAccount]: [],
  [ApiModel.ReportTemplateTrmWalletScreening]: [],
  tags: [],
  templates: [],
  themes: [],
  'theme-sets': [],
  'transaction-identifiers': [],
  'transaction-identifiers-unique': [],
  'transaction-types': [],
  'transaction-statuses': [],
  users: [],
  'verification-template-versions': [],
  'verification-templates': [],
  workflows: [],
});

export const useWorkflowActionMeta = () => useContext(WorkflowActionMetaContext);
// workflows and case actions share an editor type, as they both directly use the WorkflowEditor component
export type WorkflowEditorType =
  | 'fle-flow-editor'
  | 'fle-workflow-editor'
  | 'workflows'
  | 'workflow-runs'
  | 'modules';

interface WorkflowActionMetaProviderProps {
  children: ReactNode;
  context: WorkflowEditorType;
  externalIntegrationVendors: ExternalIntegrationVendor[];
}

const WorkflowActionMetaProvider = (props: WorkflowActionMetaProviderProps & PropsFromRedux) => {
  useApiLoader({
    loader: async () => {
      const listApiCall = props.loadLists({
        currentEnvironment: props.currentEnvironment,
        filters: {},
        sortParams: { sort: '-', shouldSortDescending: false },
      });

      const apiCalls =
        props.context !== 'fle-flow-editor'
          ? [
              props.loadAccountStatuses(),
              props.loadAccountTypes(),
              props.loadCaseTemplatesList({
                currentPage: 1,
                pageSize: 100,
                sortParams: { sort: '-', shouldSortDescending: false },
                filters: {},
              }),
              props.loadCaseQueuesList({
                currentPage: 1,
                pageSize: 100,
                sortParams: { sort: '-', shouldSortDescending: false },
                filters: { status: ['active'] },
              }),
              props.loadExternalIntegrations(),
              props.loadGraphsAndGraph(props.currentEnvironment?.id),
              props.loadGraphQueryTemplates({ status: [GraphQueryTemplateStatus.Active] }),
              props.loadNewAndLegacyInquiryTemplateList(),
              props.loadReportTemplatesList({
                currentPage: 1,
                pageSize: 100,
                sortParams: { sort: '-', shouldSortDescending: false },
                filters: {},
              }),
              props.loadTags('all'),
              props.loadThemeSets({
                omni: undefined,
                sortParams: { sort: 'updated_at', shouldSortDescending: true },
              }),
              props.loadVerificationTemplates({
                filters: { status: [VerificationTemplateStatus.Active] },
              }),
              props.loadTransactionTypesList(),
              props.loadTransactionStatuses(),
            ]
          : [];
      const additionalApiCalls = !['fle-flow-editor', 'fle-workflow-editor'].includes(props.context)
        ? [
            props.loadThemeList(),
            props.loadUserList(),
            props.loadWorkflows({
              triggerTypes: [ApiModel.TriggerSchedule],
            }),
          ]
        : [];

      return Promise.all([listApiCall, ...apiCalls, ...additionalApiCalls]).then(() => {
        switch (props.context) {
          case 'workflows':
            return props.validateWorkflow(props.externalIntegrationVendors);
          case 'modules':
            return props.validateWorkflowBlock();
        }
      });
    },
    onError: (error) => {
      if (isPermissionsError(error)) {
        // intentionally omit error display, as the full action meta fetch requires a ton of different permissions
        // and is difficult to communicate accurately to the user. prevents default error from being displayed.
      } else {
        props.showMessage('Failed to load workflow data.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
    deps: [],
  });

  return (
    <WorkflowActionMetaContext.Provider value={props.actionMeta}>
      {props.children}
    </WorkflowActionMetaContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  actionMeta: getWorkflowActionMeta(state),
  currentEnvironment: state.currentEnvironment.currentEnvironment,
});

const mapDispatchToProps = {
  loadAccountStatuses,
  loadAccountTypes,
  loadCaseTemplatesList,
  loadCaseQueuesList,
  loadExternalIntegrations,
  loadGraphsAndGraph,
  loadGraphQueryTemplates,
  loadLists,
  loadNewAndLegacyInquiryTemplateList,
  loadReportTemplatesList,
  loadTags,
  loadThemeList,
  loadThemeSets,
  loadTransactionStatuses,
  loadTransactionStatusesByTransactionType,
  loadTransactionTypesList,
  loadUserList,
  loadVerificationTemplates,
  loadWorkflows,
  showMessage,
  validateWorkflow,
  validateWorkflowBlock,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(WorkflowActionMetaProvider);
